import { Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import LoaderPage from "./pages/LoaderPage";
import AppShell from "./components/AppShell";
const Leads = lazy(() => import("./pages/Leads/Leads"));
const AccountOnHold = lazy(() => import("./components/user/AccountOnHold"));
const InvoiceView = lazy(() => import("./pages/Billing/InvoiceView"));
const PropertiesReport = lazy(() => import("./pages/Reports/ReportsItems/PropertiesReport"));
const Payments = lazy(() => import("./pages/Billing/Payments"));
const Login = lazy(() => import("./pages/Auth/Login"));
const Register = lazy(() => import("./pages/Auth/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const HomePage = lazy(() => import("./pages/HomePage"));
const Properties = lazy(() => import("./pages/Properties/Properties"));
const UnauthorizedAccessPage = lazy(() => import("./pages/Auth/UnauthorisedAccessPage"));
const PageNotExists = lazy(() => import("./pages/Auth/PageNotExists"));
const PropertyGroupPage = lazy(() => import("./pages/Properties/PropertyGroupPage"));
const RentalApplications = lazy(() => import("./pages/RentalApplications/RentalApplications"));
const Agencies = lazy(() => import("./pages/Agencies/Agencies"));
const AgencyPage = lazy(() => import("./pages/Agencies/AgencyPage"));
const MyAgency = lazy(() => import("./pages/Agencies/MyAgency"));
const Landlords = lazy(() => import("./pages/Landlord/Landlords"));
const LandlordPage = lazy(() => import("./pages/Landlord/LandlordPage"));
const SalesUserPage = lazy(() => import("./pages/Accounts/SalesUserPage"));
const PropertyPage = lazy(() => import("./pages/Properties/PropertyPage"));
const PropertyGroupDetailsPage = lazy(() => import("./pages/Properties/PropertyGroupDetailsPage"));
const TenantPage = lazy(() => import("./pages/Tenant/TenantPage"));
const Rentals = lazy(() => import("./pages/Rentals/Rentals"));
const Tenant = lazy(() => import("./pages/Tenant/Tenant"));
const RentalApplication = lazy(() => import("./pages/RentalApplications/RentalApplicationForm"));
const RentalApplicationDetails = lazy(
  () => import("./pages/RentalApplications/RentalApplicationDetails")
);
const MyProfile = lazy(() => import("./pages/User/MyProfile"));
const Settings = lazy(() => import("./pages/User/Settings"));
const FileViewerPage = lazy(() => import("./pages/FileViewerPage"));
const DocumentsPage = lazy(() => import("./pages/Documents/DocumentsPage"));
const NewRental = lazy(() => import("./pages/Rentals/NewRental"));
const Maintenance = lazy(() => import("./pages/Maintenance/Maintenance"));
const AccountUsersPage = lazy(() => import("./pages/Accounts/AccountUsersPage"));
const SalesUsersPage = lazy(() => import("./pages/Accounts/SalesUsersPage"));
const EventLogsPage = lazy(() => import("./pages/EventLogs/EventLogsPage"));
const ErrorLogsPage = lazy(() => import("./pages/EventLogs/ErrorLogsPage"));
const ErrorLog = lazy(() => import("./pages/admin/ErrorLog"));
const MaintenanceView = lazy(() => import("./pages/Maintenance/MaintenanceView"));
const Roles = lazy(() => import("./pages/Roles/Roles"));
const RolesPermissions = lazy(() => import("./pages/Roles/RolesPermissions"));

const Plans = lazy(() => import("./pages/admin/Plans"));
const Notifications = lazy(() => import("./pages/Notifications/Notifications"));
const EmailConfirmation = lazy(() => import("./pages/Auth/EmailConfirmation"));
const VerifyEmailOtp = lazy(() => import("./pages/Auth/VerifyEmailOtp"));
const Reports = lazy(() => import("./pages/Reports/Reports"));
const RegisterSuccessfulPage = lazy(() => import("./pages/Auth/RegisterSuccessfulPage"));
const ResetPasswordEmail = lazy(() => import("./pages/Auth/ResetPasswordEmail"));
const ResetPassword = lazy(() => import("./pages/Auth/ResetPassword"));
const Support = lazy(() => import("./pages/Support/Support"));
const SupportView = lazy(() => import("./pages/Support/SupportView"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const GeneralInvoice = lazy(() => import("./pages/admin/GeneralInvoice"));
const ScrollToTop = lazy(() => import("./components/ScrollToTop/ScrollToTop"));
const Payment = lazy(() => import("./pages/Payments/Payment"));
const ChoosePackage = lazy(() => import("./pages/Payments/ChoosePackage"));
const ExpensesPage = lazy(() => import("./pages/Maintenance/ExpensesPage"));
const LeaseAgreements = lazy(() => import("./pages/LeaseAgreements/LeaseAgreements"));
const EnterprisePlusOnboarding = lazy(() => import("./pages/Onboarding/EnterprisePlusOnboarding"));
const OnboardingSubmissions = lazy(() => import("./pages/Onboarding/OnboardingSubmissions"));
const SalesDashboard = lazy(() => import("./pages/Sales/SalesDashboard"));
const SalesCodes = lazy(() => import("./pages/Sales/SalesCodes"));
const TrialCodes = lazy(() => import("./pages/Sales/TrialCodes"));
const Discounts = lazy(() => import("./pages/Sales/Discounts"));

interface IRoute {
  path: string;
  component: React.ComponentType<any>;
  isAuthenticated?: boolean;
}

const routes: IRoute[] = [
  /* UI */
  {
    path: "/",
    component: HomePage
  },
  {
    path: "/dashboard",
    component: Dashboard
  },
  {
    path: "/login",
    component: Login
  },
  {
    path: "/register",
    component: Register
  },
  {
    path: "/register-successful",
    component: RegisterSuccessfulPage
  },
  {
    path: "/my-profile",
    component: MyProfile
  },
  {
    path: "/settings",
    component: Settings
  },
  {
    path: "/verify/:token",
    component: EmailConfirmation
  },
  // The code half of email verification. The signed link above still works —
  // it is what the admin button on the users form sends — but a fresh
  // registration is emailed six digits and lands here instead.
  {
    path: "/verify-email",
    component: VerifyEmailOtp
  },
  {
    path: "/reset-password-email",
    component: ResetPasswordEmail
  },
  {
    path: "/reset_password/:token/:id",
    component: ResetPassword
  },
  /* Admin */
  {
    path: "/agencies",
    component: Agencies
  },
  {
    path: "/my-agency",
    component: MyAgency
  },
  {
    path: "/landlords",
    component: Landlords
  },
  {
    // Same page, other role. Which one it manages is read from the path — see
    // Landlords.tsx.
    path: "/agency-landlords",
    component: Landlords
  },
  // Single record pages, so a link from another record can open one in a new tab.
  {
    path: "/agency/:id",
    component: AgencyPage
  },
  {
    path: "/landlord/:id",
    component: LandlordPage
  },
  {
    path: "/sales/:id",
    component: SalesUserPage
  },
  {
    path: "/property/:id",
    component: PropertyPage
  },
  {
    path: "/property-group/:id",
    component: PropertyGroupDetailsPage
  },
  {
    path: "/tenant/:id",
    component: TenantPage
  },
  {
    path: "/roles",
    component: Roles
  },
  {
    path: "/role-permissions/:role_id",
    component: RolesPermissions
  },
  {
    path: "/leads",
    component: Leads
  },
  {
    path: "/plans",
    component: Plans
  },
  {
    path: "/support",
    component: Support
  },
  {
    path: "/support/view/:id",
    component: SupportView
  },
  {
    path: "/payments",
    component: Payments
  },
  {
    path: "/invoices/:invoice_id",
    component: InvoiceView
  },

  /* Admin & Landlord */
  {
    path: "/admin-users",
    component: AdminUsers
  },
  {
    path: "/create-invoice",
    component: GeneralInvoice
  },
  {
    path: "/account-users",
    component: AccountUsersPage
  },
  {
    path: "/sales-users",
    component: SalesUsersPage
  },
  {
    path: "/account-on-hold",
    component: AccountOnHold
  },
  {
    path: "/property-group",
    component: PropertyGroupPage
  },
  {
    path: "/properties",
    component: Properties
  },
  {
    path: "/rental-applications",
    component: RentalApplications
  },
  {
    path: "/rentals",
    component: Rentals
  },
  {
    path: "/tenants",
    component: Tenant
  },
  {
    path: "/unauthorized",
    component: UnauthorizedAccessPage
  },
  {
    path: "/error-logs",
    component: ErrorLogsPage
  },
  {
    path: "/event-logs",
    component: EventLogsPage
  },
  {
    path: "/reports",
    component: Reports
  },
  {
    path: "/reports/properties-report",
    component: PropertiesReport
  },
  {
    path: "/error-log",
    component: ErrorLog
  },

  /* Landlord and Tenant */

  {
    path: "/documents",
    component: DocumentsPage
  },
  {
    path: "/notifications",
    component: Notifications
  },
  {
    path: "/maintenance",
    component: Maintenance
  },
  {
    path: "/new-rental/:rental_application_id",
    component: NewRental
  },
  {
    //path: "/new-maintenance/:rental_id",
    path: "/maintenance/:id",
    component: MaintenanceView
  },
  /* Tenant */
  {
    path: "/rental-application/:property_id/:rental_application_code",
    component: RentalApplication
  },
  {
    path: "/rental-application-details/:id/:user_id",
    component: RentalApplicationDetails
  },
  {
    path: "/view-file/:file_id",
    component: FileViewerPage
  },
  /* PAYMENTS */
  {
    path: "/payment",
    component: Payment
  },
  { path: "/plans-upgrade", component: ChoosePackage },
  {
    path: "/expenses",
    component: ExpensesPage
  },
  {
    path: "/lease-agreements",
    component: LeaseAgreements
  },
  {
    path: "/enterprise-onboarding",
    component: EnterprisePlusOnboarding
  },
  {
    path: "/onboarding-submissions",
    component: OnboardingSubmissions
  },
  {
    path: "/sales-dashboard",
    component: SalesDashboard
  },
  {
    path: "/sales-codes",
    component: SalesCodes
  },
  {
    path: "/trial-codes",
    component: TrialCodes
  },
  {
    path: "/discounts",
    component: Discounts
  }
];

// Pages that must not get the dashboard shell: the public site, the auth
// screens, and the few pages that bring their own layout.
const PUBLIC_PATHS = new Set([
  "/",
  "/login",
  "/register",
  "/register-successful",
  "/verify/:token",
  "/verify-email",
  "/reset-password-email",
  "/reset_password/:token/:id",
  "/unauthorized",
  "/rental-application/:property_id/:rental_application_code",
  "/payment"
]);

const shellRoutes = routes.filter(route => !PUBLIC_PATHS.has(route.path));
const publicRoutes = routes.filter(route => PUBLIC_PATHS.has(route.path));

function Routers() {
  function pageLoader() {
    return <LoaderPage />;
  }

  function renderRoute(route: IRoute) {
    const Component: React.ComponentType<any> = route.component;
    return <Route key={route.path} path={route.path} element={<Component />} />;
  }

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={pageLoader()}>
        <Routes>
          {/* A single shell for every dashboard page. It stays mounted across
              navigations, so the sidebar and header no longer unmount and only
              the content area swaps. */}
          <Route element={<AppShell />}>{shellRoutes.map(renderRoute)}</Route>
          {publicRoutes.map(renderRoute)}
          <Route
            path="*" // A wildcard route to catch any unmatched paths
            element={<PageNotExists />}
          />
        </Routes>
      </Suspense>
    </>
  );
}

export default Routers;
